import { rem } from 'polished'
import type { ComponentProps, MouseEvent, MouseEventHandler, PropsWithChildren } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import IconButton from 'LuxKit/Button/IconButton'
import type { DropdownListCloseHandler } from 'LuxKit/Dropdown/List/DropdownList'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'

/**
 * Stand-in header for sheets without a title: just a close button, since a sheet
 * with no header would otherwise only be dismissible by tapping the backdrop.
 * No grabber bar — these sheets are not draggable, unlike `ChatMapOffersDrawer`.
 */
const DismissRow = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: end;
  padding: ${rem(8)} ${rem(8)} 0;
`

const SafeAreaSpacer = styled.div`
  padding-block-end: env(safe-area-inset-bottom);
`

/**
 * Carries the caller's `data-*` attributes. `ModalBase` drops them, so they hang
 * off a layout-neutral element instead of disappearing on mobile.
 */
const DataAttributeCarrier = styled.div`
  display: contents;
`

export type MobileDropdownContentPadding = 'list' | 'default' | 'none'

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  open?: boolean
  /**
   * Rendered as the sheet header. Without one the sheet falls back to a bare
   * close button, which keeps simple action menus feeling light.
   */
  title?: ComponentProps<typeof ModalHeader>['title']
  subtitle?: ComponentProps<typeof ModalHeader>['subtitle']
  /** Extra content rendered beneath the header title. Requires a title. */
  headerExtension?: ComponentProps<typeof ModalHeader>['children']
  /** Shows a back button in the header. Requires a title. */
  onBackClick?: ComponentProps<typeof ModalHeader>['onBackButtonClick']
  /**
   * How the sheet body pads its content.
   *
   * `list` => edge to edge list items, for action menus.
   *
   * `default` => standard modal gutters, for panels.
   *
   * `none` => no padding, for content that manages its own gutters.
   */
  contentPadding?: MobileDropdownContentPadding
  footerContent?: ComponentProps<typeof ModalFooter>['children']
  footerActions?: ComponentProps<typeof ModalFooter>['children']
  primaryActionProps?: ComponentProps<typeof ModalFooter>['primaryActionProps']
  isPrimaryActionHidden?: boolean
  secondaryActionProps?: ComponentProps<typeof ModalFooter>['secondaryActionProps']
  isSecondaryActionHidden?: boolean
  footerActionsLayout?: ComponentProps<typeof ModalFooter>['actionsLayout']
  onClose?: DropdownListCloseHandler
}

/**
 * Bottom sheet used on mobile in place of a desktop style floating dropdown.
 *
 * This is LuxKit's own bottom sheet: `ModalBase` in `mode="default"`, which
 * slides up from the bottom on mobile and becomes a centred card from tablet up.
 * Its styling is left alone so these sheets look like every other LuxKit sheet.
 *
 * Dismissal mirrors the floating dropdown: backdrop taps and the escape key
 * report an `outside-click`, the header close button reports a `dismiss-click`,
 * and selecting an item is left to the caller so behaviour matches desktop.
 */
function MobileDropdownBottomSheet(props: Props) {
  const {
    open,
    title,
    subtitle,
    headerExtension,
    onBackClick,
    contentPadding = 'list',
    children,
    footerContent,
    footerActions,
    primaryActionProps,
    isPrimaryActionHidden,
    secondaryActionProps,
    isSecondaryActionHidden,
    footerActionsLayout,
    onClose,
    ...dataAttributes
  } = props
  const intl = useIntl()

  const handleRequestClose = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    onClose?.(event as MouseEvent<HTMLElement>, 'outside-click')
  }, [onClose])

  const handleDismissClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    onClose?.(event, 'dismiss-click')
  }, [onClose])

  const closeLabel = intl.formatMessage({
    id: 'YSnVBRVoS55xiuZI6oMW',
    defaultMessage: 'Close',
    description: 'Accessible label for the close button on a trip planner mobile bottom sheet',
  })

  const hasPrimaryAction = !isPrimaryActionHidden && !!primaryActionProps?.children
  const hasSecondaryAction = !isSecondaryActionHidden && !!secondaryActionProps?.children
  const hasFooter = !!(footerContent || footerActions || hasPrimaryAction || hasSecondaryAction)

  return <ModalBase
    isOpen={open}
    mode="default"
    size="S"
    height="auto"
    dismissible
    onClose={handleRequestClose}
  >
    {title ? <ModalHeader
      title={title}
      subtitle={subtitle}
      onBackButtonClick={onBackClick}
      onCloseButtonClick={handleDismissClick}
    >
      {headerExtension}
    </ModalHeader> : <DismissRow>
      <IconButton
        kind="tertiary"
        variant="default"
        size="small"
        aria-label={closeLabel}
        onClick={handleDismissClick}
      >
        <LineTimesIcon />
      </IconButton>
    </DismissRow>}
    <ModalBody>
      <ModalContent
        flush={contentPadding === 'none'}
        flushListItems={contentPadding === 'list'}
      >
        <DataAttributeCarrier {...dataAttributes}>
          {children}
        </DataAttributeCarrier>
      </ModalContent>
      {!hasFooter && <SafeAreaSpacer />}
    </ModalBody>
    {hasFooter && <ModalFooter
      primaryActionProps={primaryActionProps}
      isPrimaryActionHidden={isPrimaryActionHidden}
      secondaryActionProps={secondaryActionProps}
      isSecondaryActionHidden={isSecondaryActionHidden}
      actionsLayout={footerActionsLayout}
    >
      {footerContent}
      {footerActions}
    </ModalFooter>}
  </ModalBase>
}

export default MobileDropdownBottomSheet
